import { z } from 'zod';
/**
 * Sites, places, and vendor waypoint aliases (DEV-17247, memory-architecture
 * §5.3) — symbolic localisation, phase 1. Names are the shared vocabulary
 * between the orchestrator and every vendor stack; coordinates stay local to
 * each robot. No coordinate math exists anywhere in this contract, by design:
 * "go to the dock" resolves a NAME in each robot's private frame, the same
 * way VDA 5050 references nav-graph node ids and never metric positions.
 *
 * `Site` resolves `SpecSection.location.mapId` by convention (mapId =
 * Site.id). `Place` supplies the place anchors and the tree distance the
 * anchored-retrieval scorer uses (DEV-17257). `PlaceAlias` is the onboarding
 * primitive: a vendor robot's own waypoint name mapped onto our Place — a
 * config screen, not a survey visit. PlaceEdge (directed traversal edges) is
 * deferred until the routing need is evidenced (DEV-17259).
 */
export const SiteKindSchema = z.enum(['INDOOR', 'OUTDOOR', 'MIXED']);
/**
 * The place kinds v1 ships. A free vocabulary at the DB level (string
 * column) so a customer taxonomy can extend it without a migration; the
 * create input restricts to the known set so a typo is a 400, not a silently
 * unmatchable kind. Exclusion zones are ordinary places with kind 'no_go'.
 */
export const placeKinds = ['zone', 'room', 'aisle', 'dock', 'no_go', 'field_block'];
export const PlaceKindSchema = z.enum(placeKinds);
// ─── Entities ────────────────────────────────────────────────────────────────
export const SiteSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    name: z.string(),
    kind: SiteKindSchema,
    createdAt: z.date(),
    updatedAt: z.date(),
    deletedAt: z.date().nullable(),
});
export const PlaceSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    siteId: z.string(),
    parentPlaceId: z.string().nullable(),
    /** Serialized wide (string): rows may predate a narrowed create vocabulary. */
    kind: z.string(),
    label: z.string(),
    createdAt: z.date(),
    updatedAt: z.date(),
    deletedAt: z.date().nullable(),
});
export const PlaceAliasSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    placeId: z.string(),
    /** Whose vocabulary: a deviceId, a vendor slug, or 'fleet'. */
    scope: z.string(),
    alias: z.string(),
    createdAt: z.date(),
    updatedAt: z.date(),
    deletedAt: z.date().nullable(),
});
// ─── Inputs ──────────────────────────────────────────────────────────────────
export const CreateSiteInputSchema = z.object({
    name: z.string().min(1).max(200),
    kind: SiteKindSchema,
});
export const UpdateSiteInputSchema = z
    .object({
    name: z.string().min(1).max(200).optional(),
    kind: SiteKindSchema.optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
export const CreatePlaceInputSchema = z.object({
    siteId: z.string().min(1),
    parentPlaceId: z.string().min(1).optional(),
    kind: PlaceKindSchema,
    label: z.string().min(1).max(200),
});
export const UpdatePlaceInputSchema = z
    .object({
    parentPlaceId: z.string().min(1).nullable().optional(),
    kind: PlaceKindSchema.optional(),
    label: z.string().min(1).max(200).optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
export const CreatePlaceAliasInputSchema = z.object({
    placeId: z.string().min(1),
    scope: z.string().min(1).max(200),
    alias: z.string().min(1).max(200),
});
// ─── Alias onboarding (the vendor waypoint import flow) ─────────────────────
/**
 * POST body for the alias auto-proposal step: the vendor robot's own
 * waypoint list, verbatim. The service matches each name against the site's
 * Places by NAME SIMILARITY only (normalized token overlap + edit distance —
 * deterministic, zero model calls) and returns ranked proposals for a person
 * to confirm. Nothing is written until the confirm call.
 */
export const ProposePlaceAliasesInputSchema = z.object({
    siteId: z.string().min(1),
    /** Whose vocabulary the aliases will belong to (deviceId / vendor / 'fleet'). */
    scope: z.string().min(1).max(200),
    waypoints: z.array(z.string().min(1).max(200)).min(1).max(500),
});
export const PlaceAliasProposalSchema = z.object({
    /** The vendor waypoint name being matched. */
    alias: z.string(),
    /** Best-match place id, or null when nothing clears the floor. */
    placeId: z.string().nullable(),
    placeLabel: z.string().nullable(),
    /** Similarity in [0,1]; a person confirms — this is a hint, not a decision. */
    confidence: z.number(),
    /** True when an alias row for (scope, alias) already exists. */
    alreadyMapped: z.boolean(),
});
/** Confirm step: exactly the proposals the person accepted. */
export const ConfirmPlaceAliasesInputSchema = z.object({
    siteId: z.string().min(1),
    scope: z.string().min(1).max(200),
    mappings: z
        .array(z.object({ alias: z.string().min(1).max(200), placeId: z.string().min(1) }))
        .min(1)
        .max(500),
});
//# sourceMappingURL=places.js.map